import { useState } from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { IconButton } from '@components/iconButton';
import { TrashAlt } from '@assets/svgs';
import { Tooltip } from '@components/tooltip';
import { ConfirmationDialog, Dialog } from '@components/dialog';
import { useSnackbar } from 'notistack';
import { ToastType } from '@components/toast/ToastType.enum';
import { useGetBORTransferRequest, useCancelPolicyBORTransfer } from '@services/bor-transfer-request';
import { getBORStatusChipProps } from './BORTransferRequests.constants';
import Loading from '@components/Loading';
import { BORTransferRequestGetResponse } from '@lio/api-types';
import { shallow } from 'zustand/shallow';
import { useStore } from '@store/GlobalStore';

type PolicyBORTransferRequest = BORTransferRequestGetResponse['policyBORTransferRequests'][number];

interface BORTransferDetailModalProps {
  borTransferRequestId: string;
  handleClose: () => void;
}

export const BORTransferDetailModal = ({ borTransferRequestId, handleClose }: BORTransferDetailModalProps) => {
  const { borTransferRequest, isLoading } = useGetBORTransferRequest(borTransferRequestId);
  const { cancelPolicyBORTransfer, isPending: isCancelling } = useCancelPolicyBORTransfer();
  const { enqueueSnackbar } = useSnackbar();
  const [{ isAdmin, isPortalAdmin }] = useStore((state) => [state.userAuth], shallow);
  const canRemovePolicy = isAdmin || isPortalAdmin;

  const [policyToRemove, setPolicyToRemove] = useState<PolicyBORTransferRequest | null>(null);

  const handleRemoveClick = (policyRequest: PolicyBORTransferRequest) => {
    setPolicyToRemove(policyRequest);
  };

  const handleRemoveConfirm = async () => {
    if (!policyToRemove) return;
    try {
      await cancelPolicyBORTransfer({
        borTransferRequestId,
        policyId: policyToRemove.policy.id,
      });
      enqueueSnackbar('Policy removed from BOR Transfer Request successfully.', {
        variant: ToastType.Success,
      });
    } catch (error: unknown) {
      let message = (error as Error)?.message ?? 'Unknown error';
      try {
        const data = await (
          error as { response?: { json: () => Promise<{ error?: { message: string } }> } }
        )?.response?.json?.();
        message = data?.error?.message ?? message;
      } catch {
        // response was not JSON, use error.message
      }
      enqueueSnackbar(`Failed to remove policy: ${message}`, {
        persist: true,
        variant: ToastType.Error,
      });
    } finally {
      setPolicyToRemove(null);
    }
  };

  const handleRemoveCancel = () => {
    setPolicyToRemove(null);
  };

  return (
    <>
      <Dialog
        open
        size='large'
        title='BOR Transfer Request'
        handleCloseDialog={handleClose}
        secondaryBtnText='Close'
        secondaryBtnAction={handleClose}
      >
        {isLoading || !borTransferRequest ? (
          <Box sx={{ p: 4 }}>
            <Loading />
          </Box>
        ) : (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 2 }}>
                Request Details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={borTransferRequest.status}
                      size='small'
                      {...getBORStatusChipProps(borTransferRequest.status)}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Created Date
                  </Typography>
                  <Typography variant='body2'>{new Date(borTransferRequest.createdAt).toLocaleDateString()}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Requested By
                  </Typography>
                  <Typography variant='body2'>
                    {borTransferRequest.requestingUser.firstName} {borTransferRequest.requestingUser.lastName}
                  </Typography>
                </Grid>
                {borTransferRequest.description && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Description
                    </Typography>
                    <Typography variant='body2'>{borTransferRequest.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Source Brokerage
                </Typography>
                <Typography variant='body2'>{borTransferRequest.sourceBrokerage.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Source Producer
                </Typography>
                <Typography variant='body2'>
                  {borTransferRequest.sourceProducer.user.firstName} {borTransferRequest.sourceProducer.user.lastName}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Target Brokerage
                </Typography>
                <Typography variant='body2'>{borTransferRequest.targetBrokerage.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Target Producer
                </Typography>
                <Typography variant='body2'>
                  {borTransferRequest.targetProducer.user.firstName} {borTransferRequest.targetProducer.user.lastName}
                </Typography>
              </Grid>
            </Grid>

            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Policies ({borTransferRequest.policyBORTransferRequests.length})
              </Typography>
              <Box
                sx={{
                  border: (theme) => theme.borders.main,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 80px',
                    px: 2,
                    py: 1,
                    borderBottom: (theme) => theme.borders.main,
                    backgroundColor: 'background.default',
                  }}
                >
                  <Typography variant='caption' fontWeight={600}>
                    Policy Number
                  </Typography>
                  <Typography variant='caption' fontWeight={600}>
                    Customer
                  </Typography>
                  <Typography variant='caption' fontWeight={600}>
                    Status
                  </Typography>
                  <Typography variant='caption' fontWeight={600}>
                    Action
                  </Typography>
                </Box>
                <Box sx={{ overflowY: 'auto', maxHeight: 'max(204px, calc(80vh - 500px))' }}>
                  {borTransferRequest.policyBORTransferRequests.length === 0 ? (
                    <Box sx={{ px: 2, py: 2 }}>
                      <Typography variant='body2' color='text.secondary'>
                        No policies in this request.
                      </Typography>
                    </Box>
                  ) : (
                    borTransferRequest.policyBORTransferRequests.map((policyRequest) => (
                      <Box
                        key={policyRequest.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr 80px',
                          alignItems: 'center',
                          px: 2,
                          py: 1,
                          borderBottom: (theme) => theme.borders.main,
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Typography variant='bodySmall'>{policyRequest.policy.policyNumber.policyNumber}</Typography>
                        <Typography variant='bodySmall'>{policyRequest.policy.customer.name}</Typography>
                        <Box>
                          <Chip
                            label={policyRequest.status}
                            size='small'
                            {...getBORStatusChipProps(policyRequest.status)}
                          />
                        </Box>
                        <Box>
                          {policyRequest.status === 'Pending' && canRemovePolicy && (
                            <Tooltip title='Remove Policy'>
                              <IconButton
                                size='small'
                                disabled={isCancelling}
                                onClick={() => handleRemoveClick(policyRequest)}
                                sx={{ p: 0.5 }}
                                aria-label='Remove policy'
                              >
                                <TrashAlt width='14px' height='14px' />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>

      <ConfirmationDialog
        isOpen={Boolean(policyToRemove)}
        description={
          policyToRemove
            ? `Are you sure you want to remove policy ${policyToRemove.policy.policyNumber.policyNumber} from this BOR Transfer Request?`
            : ''
        }
        isLoading={isCancelling}
        onCancel={handleRemoveCancel}
        buttonConfig={{
          confirmText: 'Remove',
          onConfirm: handleRemoveConfirm,
          secondaryText: 'Cancel',
          onSecondaryButtonClick: handleRemoveCancel,
        }}
      />
    </>
  );
};
